import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useSnackbar } from "notistack"
import axiosInstance from "./axiosInstance"
import { AUTH_TOKEN } from "constants/AuthConstant"

import { enqueueRequest } from "./offline/queue"
import { getByIdOffline } from "utils/offline/Get_one"
import {
  upsertOffline,
  deleteOffline,
  mergeUpsertOffline,
} from "utils/offline/crud.js"
import { STORES } from "utility"
import { RESOURCE_TO_STORE } from "utils/offline/map"
import { OFFLINE_HANDLERS } from "./offline/create/exptions"

import * as PatientsOffline from "utils/offline/Patients"
import * as ConsultationOffline from "utils/offline/Consultation"
import * as MedicinesOffline from "utils/offline/Medicines"
import * as JustificationOffline from "utils/offline/Justification"
import * as LettresOrientationOffline from "utils/offline/LettresOrientation"
import * as CertificatsMedicauxOffline from "utils/offline/Certificats_medicaux"
import * as ArretsTravailOffline from "utils/offline/Arrets_travail"
import * as BilansOffline from "utils/offline/Bilans"
import * as PrescriptionOffline from "utils/offline/Prescription"
import * as PaymentsOffline from "utils/offline/Payments"
import * as PrestationsOffline from "utils/offline/Prestations"
import * as WaitingListOffline from "utils/offline/WaitingList"
import * as AppointmentsOffline from "utils/offline/Appointments"
import * as RemindersOffline from "utils/offline/Reminders"
import * as UsersOffline from "utils/offline/Users"
import * as StatisticsOffline from "utils/offline/Statistics"
import * as ExpensesOffline from "utils/offline/Expenses"
import { getProfileOffline, cacheProfilAfterOnlineSave } from "utils/offline/Profile"

const OFFLINE_GET_ALL = {
  "patient/getAll": PatientsOffline.getPatientsOffline,
  "consultation/getAll": ConsultationOffline.getConsultationsOffline,
  "medicine/getAll": MedicinesOffline.getMedicinesOffline,
  "justification/getAll": JustificationOffline.getJustificationsOffline,
  "orientationLetter/getAll":
    LettresOrientationOffline.getLettresOrientationOffline,
  "medicalCertificate/getAll":
    CertificatsMedicauxOffline.getCertificatsMedicauxOffline,
  "workStopping/getAll": ArretsTravailOffline.getArretsTravailOffline,
  "bilan/getAll": BilansOffline.getBilansOffline,
  "prescription/getAll": PrescriptionOffline.getPrescriptionsOffline,
  "prescription/qsp": PrescriptionOffline.getAllQspOffline,
  "prescription/genre": PrescriptionOffline.getAllGnreOffline,
  "bilan/allTests": BilansOffline.getAllTestsOffline,
  "payment/getAll": PaymentsOffline.getPaymentsOffline,
  "prestation/getAll": PrestationsOffline.getPrestationsOffline,
  "waitingList/getAll": WaitingListOffline.getWaitingListOffline,
  "appointment/getAll": AppointmentsOffline.getAppointmentsOffline,
  "reminder/getAll": RemindersOffline.getRemindersOffline,
  "user/getAll": UsersOffline.getUsersOffline,
  "expenses/getAll": ExpensesOffline.getExpensesOffline,
  "doctorOffice/statistics": StatisticsOffline.getStatisticsOffline,
  "doctorOffice/statistics/chartData": StatisticsOffline.getChartDataOffline,
  "doctorOffice/statistics/chiffre-affaire":
    StatisticsOffline.getChiffreAffaireOffline,
  "waitingList/metrics/today": StatisticsOffline.getWaitingMetricsTodayOffline,
}

// ✅ Bug 9 fix: on utilise STORES.X au lieu de strings littéraux
const OFFLINE_GET_BY_ID = {
  "patient/getById": STORES.PATIENTS,
  "consultation/getById": STORES.CONSULTATIONS,
  "medicine/getById": STORES.MEDICINE,
  "justification/getById": STORES.JUSTIFICATIONS,
  "orientationLetter/getById": STORES.LETTRES_ORIENTATION,
  "medicalCertificate/getById": STORES.CERTIFICATS,
  "workStopping/getById": STORES.ARRETS_TRAVAIL,
  "bilan/getById": STORES.BILANS,
  "prescription/getById": STORES.PRESCRIPTIONS,
  "waitingList/getById": STORES.WAITING_LIST,
  "reminder/getById": STORES.REMINDERS,
  "user/getById": STORES.USERS,
  "expenses/getById": STORES.EXPENSES,
}

// ============================================================
// GET
// ============================================================
export const useGet = ({
  path,
  query = {},
  options = {},
  authorization = false,
  headers = {},
  successMessage,
  invalidateQueries = null,
}) => {
  const pathArray = path.split("/")
  const token = localStorage.getItem(AUTH_TOKEN)
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useQuery({
    queryKey: [...pathArray, query],
    queryFn: async ({ signal }) => {
      if (!path || path.includes("null") || path.includes("undefined")) {
        return null
      }
      const isOffline =
        localStorage.getItem("isOffline") === "true" || !navigator.onLine

      if (isOffline) {
        // Certains chemins embarquent leur query string (ex:
        // "doctorOffice/statistics/chiffre-affaire?type=mois&month=6").
        // On sépare la base pour le lookup et on fusionne les params embarqués
        // avec l'objet `query` pour les passer aux handlers offline.
        const [basePath, qs] = path.split("?")
        const embeddedParams = qs
          ? Object.fromEntries(new URLSearchParams(qs))
          : {}
        const mergedFilters = { ...embeddedParams, ...query }

        if (OFFLINE_GET_ALL[basePath]) {
          return OFFLINE_GET_ALL[basePath]({
            page: query.page,
            limit: query.limit,
            filters: mergedFilters,
          })
        }

        const byIdEntry = Object.entries(OFFLINE_GET_BY_ID).find(([prefix]) =>
          basePath.startsWith(prefix),
        )
        if (byIdEntry) {
          const [, store] = byIdEntry
          const id = basePath.split("/").pop()
          return getByIdOffline(store, id)
        }

        if (basePath.includes("profil")) {
          return getProfileOffline()
        }

        return null
      }

      const res = await axiosInstance.get(path, {
        params: query,
        signal,
        timeout: 8000,
        headers: {
          ...(authorization && token && { Authorization: `Bearer ${token}` }),
          ...headers,
        },
      })

      return res.data
    },
    enabled: options.enable === false ? false : authorization ? !!token : true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // On a network error, axiosInstance flips localStorage.isOffline to "true".
    // Retrying once then makes the queryFn take the IndexedDB (offline) branch and
    // resolve with cached data — so the query self-heals instead of hanging on a spinner.
    retry: (failureCount, error) => {
      if (!error?.response) return failureCount < 1
      return failureCount < 2
    },
    retryDelay: (_attempt, error) => {
      // Network error → retry instantly (offline branch will take over).
      if (!error?.response) return 0
      return Math.min(1000 * 2 ** _attempt, 5000)
    },

    onSuccess: (data) => {
      if (successMessage) {
        enqueueSnackbar(successMessage, {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          autoHideDuration: 2000,
        })
      }
      if (invalidateQueries) {
        queryClient.invalidateQueries({
          queryKey: invalidateQueries ?? pathArray[0],
        })
      }
      options.onSuccess?.(data)
    },
    onError: (error) => {
      enqueueSnackbar(
        error?.response?.data?.error ?? "Erreur lors du chargement des données",
        { variant: "error", autoHideDuration: 3000 },
      )
      options.onError?.(error)
    },

    ...options,
  })
}

// ============================================================
// POST
// ============================================================
function parsePath(path) {
  const parts = path.split("/")
  return {
    resource: parts[0],
    action: parts[1],
    id: parts[2] ?? null,
  }
}

export const usePost = ({
  path,
  options,
  method = "post",
  authorization = false,
  successMessage,
  invalidateQueries = null,
}) => {
  const queryClient = useQueryClient()
  const pathArray = path.split("/")
  const { enqueueSnackbar } = useSnackbar()
  const token = localStorage.getItem(AUTH_TOKEN)

  const mutation = useMutation({
    mutationFn: async (body) => {
      const isDelete = method.toLowerCase() === "delete"
      const isOffline =
        localStorage.getItem("isOffline") === "true" || !navigator.onLine

      if (isOffline) {
        // ✅ Bug 6 fix (best-effort): on track les écritures cache pour rollback en cas d'échec
        const cacheRollbacks = []

        try {
          const { resource, action } = parsePath(path)

          // 💰 ADD PAYMENT
          if (resource === "consultation" && action === "addPayment") {
            const consultationId = path.split("/").pop()
            const existingConsultation = await getByIdOffline(
              STORES.CONSULTATIONS,
              consultationId,
            )

            const consultationUpdate = {
              ...existingConsultation,
              patientId: existingConsultation?.patientId,
              id: consultationId,
              amountToPay: body.montantAPayer,
              amountPaid: body.montantPaye,
              remainingAmount: body.montantRestant,
              updatedAt: new Date().toISOString(),
              __offline: true,
            }

            await mergeUpsertOffline(STORES.CONSULTATIONS, consultationUpdate, {
              markOffline: true,
            })
            cacheRollbacks.push({
              type: "restore",
              store: STORES.CONSULTATIONS,
              previous: existingConsultation,
            })

            const payment = {
              id: crypto.randomUUID(),
              consultationId,
              montant: body.montantAPayer,
              payment: body.montantPaye,
              leftToPay: body.montantRestant,
              date: new Date().toISOString(),
              __offline: true,
            }

            await mergeUpsertOffline(STORES.PAYMENTS, payment, {
              markOffline: true,
            })
            cacheRollbacks.push({
              type: "delete",
              store: STORES.PAYMENTS,
              id: payment.id,
            })

            // ✅ Bug 2 fix: on enrichit le body avec __offline pour la sync
            // ✅ __cleanup: tempId du payment que la sync devra supprimer
            await enqueueRequest({
              id: crypto.randomUUID(),
              path,
              method,
              body: { ...body, __offline: true },
              __cleanup: [{ store: STORES.PAYMENTS, id: payment.id }],
              createdAt: new Date().toISOString(),
            })

            return {
              consultation: consultationUpdate,
              payment,
              __offline: true,
            }
          }

          // 📋 WAITING LIST STATUS  — path format: waitingList/{id}/status
          if (resource === "waitingList" && path.endsWith("/status")) {
            const id = action // parts[1] is the entity id, parts[2] is "status"
            const previous = await getByIdOffline(STORES.WAITING_LIST, id)

            const updated = {
              id,
              status: body.status,
              updatedAt: new Date().toISOString(),
              __offline: true,
            }

            await mergeUpsertOffline(STORES.WAITING_LIST, updated, {
              markOffline: true,
            })
            cacheRollbacks.push({
              type: "restore",
              store: STORES.WAITING_LIST,
              previous,
            })

            // ✅ Bug 2 fix: __offline dans le body
            await enqueueRequest({
              id: crypto.randomUUID(),
              path,
              method,
              body: { ...body, __offline: true },
              createdAt: new Date().toISOString(),
            })

            return updated
          }

          const storeKey = RESOURCE_TO_STORE[resource]
          if (!storeKey) {
            throw new Error(`Offline store not mapped for ${resource}`)
          }
          const store = STORES[storeKey]

          // 🗑️ DELETE
          if (action === "delete" || action === "deleteById") {
            const id = path.split("/").pop()
            const previous = await getByIdOffline(store, id)
            await deleteOffline(store, id)
            cacheRollbacks.push({ type: "restore", store, previous })

            await enqueueRequest({
              id: crypto.randomUUID(),
              path,
              method,
              body: { id, __offline: true },
              createdAt: new Date().toISOString(),
            })
            return { id, __offline: true }
          }

          // ➕ CREATE / ✏️ UPDATE
          let resultData = null

          if (
            OFFLINE_HANDLERS[resource] &&
            OFFLINE_HANDLERS[resource][action]
          ) {
            // Le handler doit déjà persister + retourner les données enrichies (avec __offline: true)
            resultData = await OFFLINE_HANDLERS[resource][action](body, path)
          } else {
            let idFromPath = null
            if (action === "update" || action === "updateById") {
              idFromPath = path.split("/").pop()
            }
            resultData = {
              ...body,
              id: body.id ?? idFromPath ?? crypto.randomUUID(),
              __offline: true,
            }
            await mergeUpsertOffline(store, resultData, { markOffline: true })
          }

          const newData = {
            ...resultData,
            ...(resultData.startDate && {
              startDate: new Date(resultData.startDate).toISOString(),
            }),
            ...(resultData.endDate && {
              endDate: new Date(resultData.endDate).toISOString(),
            }),
          }

          await enqueueRequest({
            id: crypto.randomUUID(),
            path,
            method,
            body: newData, // contient déjà __offline: true via resultData
            createdAt: new Date().toISOString(),
          })

          return resultData
        } catch (err) {
          // Rollback best-effort (pas d'atomicité IndexedDB native sur multi-stores)
          for (const rb of cacheRollbacks.reverse()) {
            try {
              if (rb.type === "restore" && rb.previous) {
                await upsertOffline(rb.store, rb.previous)
              } else if (rb.type === "delete" && rb.id) {
                await deleteOffline(rb.store, rb.id)
              }
            } catch (_rbErr) {
              // rollback failure is best-effort, nothing to do
            }
          }
          enqueueSnackbar(
            "Impossible de sauvegarder en mode hors ligne. Réessayez.",
            { variant: "error", autoHideDuration: 3000 },
          )
          throw err
        }
      }

      // ============ ONLINE ============
      const headers = {
        ...(authorization &&
          token && {
            headers: {
              Authorization: `Bearer ${token}`,
              "Access-Control-Allow-Origin": "*",
            },
          }),
      }

      if (body && isDelete) {
        return axiosInstance[method](path, { ...headers, data: { ...body } })
      }
      return axiosInstance[method](
        path,
        isDelete ? headers : body,
        !isDelete && headers,
      )
    },
    onSuccess: async (responseData, _variables) => {
      const { resource, action } = parsePath(path)

      if (successMessage) {
        enqueueSnackbar("Success", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          autoHideDuration: 2000,
        })
      }

      try {
        // 💰 ADD PAYMENT (online success)
        if (resource === "consultation" && action === "addPayment") {
          const consultation =
            responseData.data?.consultation ||
            responseData.data?.data?.consultation
          const payment =
            responseData.data?.payment || responseData.data?.data?.payment

          // ✅ Bug 1 fix: markOffline: false pour clear le flag explicitement
          if (consultation) {
            await mergeUpsertOffline(STORES.CONSULTATIONS, consultation, {
              markOffline: false,
            })
          }
          if (payment) {
            await mergeUpsertOffline(STORES.PAYMENTS, payment, {
              markOffline: false,
            })
          }
          return
        }

        // 🩺 EDIT DOCTOR PROFILE (online success) — action perso hors du
        // create/update/updateById générique ci-dessous, on rafraîchit donc
        // le cache IndexedDB ici pour que le mode hors ligne reflète la
        // dernière sauvegarde en ligne.
        if (resource === "doctorOffice" && action === "editMyProfil") {
          if (_variables instanceof FormData) {
            await cacheProfilAfterOnlineSave(_variables)

            // cacheProfilAfterOnlineSave ignore les fichiers, donc le nouveau
            // logo (envoyé en File) n'atteint jamais la copie hors ligne du
            // profil. On récupère son nom généré par le serveur dans la réponse
            // et on le persiste, pour que le mode hors ligne l'affiche.
            const uploadedLogo = _variables.get("logo")
            if (uploadedLogo instanceof File || uploadedLogo instanceof Blob) {
              const body = responseData?.data
              const savedProfile =
                body?.doctorOffice ||
                body?.data?.doctorOffice ||
                body?.profile ||
                body?.data ||
                body
              if (savedProfile?.logo) {
                const existing = await getProfileOffline()
                await mergeUpsertOffline(
                  STORES.PROFILE,
                  { id: existing?.id ?? "me", logo: savedProfile.logo },
                  { markOffline: false },
                )
              }
            }
          }
          queryClient.invalidateQueries({
            queryKey: invalidateQueries ?? pathArray[0],
          })
          return
        }

        const storeKey = RESOURCE_TO_STORE[resource]
        if (storeKey) {
          const store = STORES[storeKey]

          if (action === "delete" || action === "deleteById") {
            const id = path.split("/").pop()
            await deleteOffline(store, id)
          }

          if (
            action === "create" ||
            action === "update" ||
            action === "updateById"
          ) {
            const entity =
              responseData.data?.data ||
              responseData.data?.patient ||
              responseData.data?.consultation ||
              responseData.data?.medicine ||
              responseData.data?.payment ||
              responseData.data?.prescription ||
              responseData.data?.bilan ||
              responseData.data?.workStoppage ||
              responseData.data?.updated ||
              responseData.data?.medicalCertificate ||
              responseData.data?.orientationLetter ||
              responseData.data?.justification

            if (entity) {
              // upsertOffline retire toujours __offline (online success)
              await upsertOffline(store, entity)
            }
          }
        }
      } catch (_e) {
        // cache update failure is non-critical
      }

      queryClient.invalidateQueries({
        queryKey: invalidateQueries ?? pathArray[0],
      })
    },
    onError: (error) => {
      enqueueSnackbar(
        error?.response?.data?.error ?? error?.response?.data?.error,
        { variant: "error", autoHideDuration: 3000 },
      )
    },
    ...options,
  })

  return mutation
}
